import React, { useState } from 'react';
import { Box, Container, Typography, AppBar, Toolbar, Link, useTheme, useMediaQuery, Menu, MenuItem, IconButton } from '@mui/material';
import { Email, LinkedIn, Instagram, Language as LanguageIcon } from '@mui/icons-material';
import { DESIGN_TOKENS } from '../design-system/tokens/colors';
import { useTranslation } from 'react-i18next';

export const MaintenancePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation('common');
  const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL || 'suporte@muu.app';

  // Language Menu State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setAnchorEl(null);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    handleLanguageClose();
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#ffffff', display: 'flex', flexDirection: 'column' }}>
      {/* Header Minimalista */}
      <AppBar 
        position="fixed" 
        elevation={0} 
        sx={{ 
          backgroundColor: 'rgba(255,255,255,0.9)', 
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${DESIGN_TOKENS.colors.neutral[100]}`
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 1.5, minHeight: '70px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <img 
                  src="/logo.svg" 
                  alt="MUU Logo" 
                  style={{ height: 32 }}
                />
            </Box>

            {/* Language Selector */}
            <Box>
              <IconButton
                onClick={handleLanguageClick}
                size="small"
                sx={{ ml: 2, color: DESIGN_TOKENS.colors.text.secondary }}
                aria-controls={open ? 'language-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
              >
                <LanguageIcon />
                <Typography variant="button" sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
                  {i18n.language.toUpperCase()}
                </Typography>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                id="language-menu"
                open={open}
                onClose={handleLanguageClose}
                onClick={handleLanguageClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.12))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={() => changeLanguage('pt')}>
                  🇧🇷 Português
                </MenuItem>
                <MenuItem onClick={() => changeLanguage('en')}>
                  🇺🇸 English
                </MenuItem>
                <MenuItem onClick={() => changeLanguage('es')}>
                  🇪🇸 Español
                </MenuItem>
              </Menu>
            </Box>
        </Toolbar>
      </AppBar>

      {/* Conteúdo Principal */}
      <Container 
        maxWidth="md" 
        sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          pt: 10, // Espaço para compensar o header fixo
          pb: 4,
          textAlign: 'center'
        }}
      >
        {/* Ícone Minimalista Elaborado */}
        <Box
            sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                backgroundColor: DESIGN_TOKENS.colors.primary[50],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 6,
                boxShadow: `0 0 0 20px ${DESIGN_TOKENS.colors.primary[50]}40` // Efeito de onda sutil
            }}
        >
             {/* Ícone de Construção/Manutenção Customizado (SVG) */}
             <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21.731 2.269a2.625 2.625 0 113.712 3.712l-2.006 2.006-3.712-3.712 2.006-2.006zM15.363 8.636l-3.712 3.712 8.636 8.636a4.5 4.5 0 01-6.364 6.364l-8.636-8.636 3.712-3.712-8.636-8.636a4.5 4.5 0 016.364-6.364l8.636 8.636z" fill={DESIGN_TOKENS.colors.primary[600]} />
                <path d="M14.5 13.5L10.5 9.5" stroke="white" strokeWidth="2" strokeLinecap="round"/>
             </svg>
        </Box>

        <Typography variant="h2" sx={{ 
            fontWeight: 800, 
            color: DESIGN_TOKENS.colors.text.primary,
            mb: 3,
            fontSize: { xs: '2rem', md: '3rem' },
            letterSpacing: '-0.02em',
            background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.text.primary} 0%, ${DESIGN_TOKENS.colors.text.secondary} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
        }}>
            {t('maintenance.title', 'Melhorando sua experiência')}
        </Typography>

        <Typography variant="h5" sx={{ 
            color: DESIGN_TOKENS.colors.text.secondary, 
            mb: 8,
            maxWidth: '600px',
            lineHeight: 1.6,
            fontWeight: 400,
            fontSize: { xs: '1rem', md: '1.25rem' }
        }}>
            {t('maintenance.description', 'Estamos realizando uma manutenção programada para trazer novos recursos e melhorias de performance. Voltaremos em breve.')}
        </Typography>

        <Box sx={{ 
            p: 3, 
            borderRadius: '16px', 
            bgcolor: '#FAFAFA', 
            border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
            maxWidth: '400px',
            width: '100%',
            transition: 'transform 0.2s ease',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }
        }}>
            <Typography variant="subtitle2" sx={{ color: DESIGN_TOKENS.colors.text.primary, fontWeight: 600, mb: 0.5 }}>
                {t('maintenance.help', 'Precisa de ajuda urgente?')}
            </Typography>
            <Link href={`mailto:${supportEmail}`} sx={{ color: DESIGN_TOKENS.colors.primary[600], textDecoration: 'none', fontWeight: 500 }}>
                {supportEmail}
            </Link>
        </Box>

      </Container>

      {/* Footer Minimalista */}
      <Box component="footer" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.tertiary, display: 'block', mb: 2 }}>
            © {new Date().getFullYear()} MUU. {t('maintenance.rights', 'Todos os direitos reservados.')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
            <Link href="#" sx={{ color: DESIGN_TOKENS.colors.text.tertiary, transition: 'color 0.2s', '&:hover': { color: DESIGN_TOKENS.colors.primary[600] } }}>
                <Instagram fontSize="small" />
            </Link>
            <Link href="#" sx={{ color: DESIGN_TOKENS.colors.text.tertiary, transition: 'color 0.2s', '&:hover': { color: DESIGN_TOKENS.colors.primary[600] } }}>
                <LinkedIn fontSize="small" />
            </Link>
            <Link href={`mailto:${supportEmail}`} sx={{ color: DESIGN_TOKENS.colors.text.tertiary, transition: 'color 0.2s', '&:hover': { color: DESIGN_TOKENS.colors.primary[600] } }}>
                <Email fontSize="small" />
            </Link>
        </Box>
      </Box>
    </Box>
  );
};
